import React, { useEffect, useState } from "react";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  TextField,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { authGet, authPut } from "../../../lib";

const useStyles = makeStyles((theme) => ({
  fields: {
    display: "grid",
    gridTemplateColumns: "repeat(12, 1fr)",
    gap: theme.spacing(1.5),
    paddingTop: theme.spacing(0.5),
    width: 560,
    maxWidth: "100%",
  },
  spacer: { flex: 1 },
  error: { color: "#c62828", fontSize: 13, marginTop: theme.spacing(1) },
  actions: { padding: theme.spacing(1, 3, 2) },
}));

/**
 * The six editable panels, as field lists. The server holds the same
 * whitelists — this one decides what to show, that one decides what may be
 * written, and neither trusts the other.
 *
 * `w` is the flex basis, so a dialog lays out without per-panel CSS.
 */
/**
 * The editable panels, as field lists on a 12-column grid.
 *
 * `col` is the span, matching the legacy modals exactly: name is 10 + MI 2,
 * city 10 + state 2, zip 6 + country 6, and everything else full width. That
 * is what makes the fields line up rather than wrap at arbitrary widths.
 *
 * The server holds the same whitelists — this one decides what to show, that
 * one decides what may be written, and neither trusts the other.
 */
export const ORDER_PANELS = {
  billing: {
    title: "Edit Billing Address",
    copyFrom: { label: "Copy from shipping", prefix: "ship", to: "bill" },
    fields: [
      { name: "bill_first_name", label: "First Name", col: 5 },
      { name: "bill_mi", label: "MI", col: 2 },
      { name: "bill_last_name", label: "Last Name", col: 5 },
      { name: "bill_business_name", label: "Company Name", col: 12 },
      { name: "bill_address1", label: "Address 1", col: 12 },
      { name: "bill_address2", label: "Address 2", col: 12 },
      { name: "bill_city", label: "City", col: 4 },
      { name: "bill_state", label: "State", col: 2 },
      { name: "bill_zip", label: "Zip", col: 3 },
      { name: "bill_country", label: "Country", col: 3 },
    ],
  },
  contact: {
    title: "Edit Contact Info",
    fields: [
      { name: "home_phone", label: "Home Phone", col: 6 },
      { name: "contact_phone", label: "Contact Phone", col: 6 },
      { name: "email", label: "Email", col: 12 },
      { name: "email_secondary", label: "Secondary Email", col: 12 },
    ],
  },
  shipping: {
    title: "Edit Shipping Address",
    copyFrom: { label: "Copy from billing", prefix: "bill", to: "ship" },
    fields: [
      { name: "ship_first_name", label: "First Name", col: 5 },
      { name: "ship_mi", label: "MI", col: 2 },
      { name: "ship_last_name", label: "Last Name", col: 5 },
      { name: "ship_business_name", label: "Company Name", col: 12 },
      { name: "ship_address1", label: "Address 1", col: 12 },
      { name: "ship_address2", label: "Address 2", col: 12 },
      { name: "ship_city", label: "City", col: 4 },
      { name: "ship_state", label: "State", col: 2 },
      { name: "ship_zip", label: "Zip", col: 3 },
      { name: "ship_country", label: "Country", col: 3 },
    ],
  },
  method: {
    title: "Edit Shipping Method",
    fields: [
      { name: "ship_rate_id", label: "Method", col: 8, type: "shipRate" },
      { name: "shipping", label: "Shipping Charged", col: 4, type: "number" },
    ],
  },
  // Opened from the subtotal row — the legacy "Edit Price" modal, with the
  // same four fields in the same order.
  adjustments: {
    title: "Edit Price",
    panel: "price",
    fields: [
      { name: "tax", label: "Sales Tax", col: 6, type: "number" },
      { name: "discount", label: "Discount", col: 6, type: "number" },
      { name: "wire_discount", label: "Wire Discount", col: 6, type: "number" },
      { name: "surcharge", label: "Surcharge", col: 6, type: "number" },
    ],
  },
  payment: {
    title: "Edit Payment",
    fields: [
      // Free text rather than a picker: every coupon in the table has expired,
      // so a "valid coupons" list would be empty. The recent ones are offered
      // as suggestions and an empty value removes the coupon.
      {
        name: "coupon_code",
        label: "Coupon Code",
        col: 6,
        type: "coupon",
        help: "Leave empty to remove the coupon",
      },
      {
        name: "return_end",
        label: "Return Expires",
        col: 6,
        type: "date",
        help: "Overrides the standard return window",
      },
      { name: "notes_payment", label: "Payment Note", col: 12, rows: 3 },
    ],
  },
  notes: {
    title: "Edit Notes",
    fields: [
      { name: "notes_verification", label: "Order Notes (internal)", col: 12, rows: 5 },
      { name: "notes_general", label: "Notes to Customer", col: 12, rows: 5 },
      { name: "notes_customer", label: "Note Shown in the Orders List", col: 12, rows: 2 },
      { name: "notes_payment", label: "Payment Note", col: 12, rows: 2 },
    ],
  },
  gift: {
    title: "Edit Gift Options & Instructions",
    fields: [
      { name: "gift_wrap", label: "Gift Wrapped", col: 12, type: "bool" },
      { name: "gift_message", label: "Gift Card Message", col: 12, rows: 2 },
      { name: "special_instructions", label: "Special Instructions", col: 12, rows: 3 },
      { name: "sizing_instructions", label: "Sizing Instructions", col: 12, rows: 2 },
    ],
  },
};

/**
 * Edits one panel of an order.
 *
 * Only fields the user actually changed are sent, so two people editing
 * different halves of the same panel do not overwrite each other, and an
 * untouched field is never rewritten with a value the screen happened to
 * render.
 */
export function OrderPanelDialog({
  panel,
  orderId,
  values,
  shipRates,
  onClose,
  onSaved,
  // When present the dialog edits in memory and hands the changed fields
  // back instead of writing — the new-order screen has nothing to write to
  // until the order exists.
  onApply,
}) {
  const classes = useStyles();
  const spec = ORDER_PANELS[panel];

  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [coupons, setCoupons] = useState([]);

  useEffect(() => {
    if (!spec?.fields.some((f) => f.type === "coupon")) return;
    authGet("/orders/coupons").then(({ data }) => setCoupons(data?.rows ?? []));
  }, [spec]);

  useEffect(() => {
    if (!spec) return;
    const seed = {};
    spec.fields.forEach((f) => {
      const v = values?.[f.name];
      seed[f.name] = f.type === "bool" ? !!Number(v) : v ?? "";
    });
    setForm(seed);
    setError("");
  }, [panel, values, spec]);

  if (!spec) return null;

  const set = (f) => (e) => {
    const v = f.type === "bool" ? e.target.checked : e.target.value;
    setForm((s) => ({ ...s, [f.name]: v }));
  };

  /**
   * Copies the counterpart address into this form — "Copy from shipping" on
   * billing and vice versa. Only fills the dialog; nothing is written until
   * Save, so a mis-click is undone by closing.
   */
  const copyFrom = () => {
    const { prefix, to } = spec.copyFrom;
    setForm((current) => {
      const next = { ...current };
      spec.fields.forEach((f) => {
        if (!f.name.startsWith(`${to}_`)) return;
        const source = f.name.replace(`${to}_`, `${prefix}_`);
        if (values?.[source] !== undefined) next[f.name] = values[source] ?? "";
      });
      return next;
    });
  };

  const save = async () => {
    const body = {};
    spec.fields.forEach((f) => {
      const before = f.type === "bool" ? !!Number(values?.[f.name]) : values?.[f.name] ?? "";
      const after = form[f.name];
      if (String(before) === String(after)) return;
      body[f.name] = f.type === "bool" ? (after ? 1 : 0) : after;
    });

    if (!Object.keys(body).length) {
      onClose();
      return;
    }

    if (onApply) {
      onApply(body);
      return;
    }

    setBusy(true);
    // A few dialogs are a subset of a server panel rather than a panel of
    // their own; `panel` on the spec names the one to write to.
    const { error: err } = await authPut(
      `/orders/${orderId}/panel/${spec.panel ?? panel}`,
      body,
    );
    setBusy(false);

    if (err) {
      setError(err.message || "Could not save.");
      return;
    }
    onSaved();
  };

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="md">
      <DialogTitle>{spec.title}</DialogTitle>
      <DialogContent>
        <div className={classes.fields}>
          {spec.fields.map((f) => {
            if (f.type === "bool") {
              return (
                <FormControlLabel
                  key={f.name}
                  style={{ gridColumn: `span ${f.col ?? 12}` }}
                  control={
                    <Checkbox
                      color="primary"
                      checked={!!form[f.name]}
                      onChange={set(f)}
                    />
                  }
                  label={f.label}
                />
              );
            }

            if (f.type === "date") {
              return (
                <TextField
                  key={f.name}
                  fullWidth
                  label={f.label}
                  type="date"
                  helperText={f.help}
                  style={{ gridColumn: `span ${f.col ?? 12}` }}
                  // MySQL DATE comes back as an ISO timestamp; the input wants
                  // a bare yyyy-mm-dd.
                  value={String(form[f.name] ?? "").slice(0, 10)}
                  onChange={set(f)}
                />
              );
            }

            if (f.type === "coupon") {
              return (
                <TextField
                  key={f.name}
                  fullWidth
                  label={f.label}
                  helperText={f.help}
                  style={{ gridColumn: `span ${f.col ?? 12}` }}
                  value={form[f.name] ?? ""}
                  onChange={set(f)}
                  inputProps={{ list: "order-coupon-codes" }}
                  InputProps={{
                    endAdornment: (
                      <datalist id="order-coupon-codes">
                        {coupons.map((c) => (
                          <option key={c.coupon_id} value={c.coupon_code}>
                            {c.coupon_name}
                          </option>
                        ))}
                      </datalist>
                    ),
                  }}
                />
              );
            }

            if (f.type === "shipRate") {
              return (
                <TextField
                  key={f.name}
                  fullWidth
                  select
                  label={f.label}
                  style={{ gridColumn: `span ${f.col ?? 12}` }}
                  value={form[f.name] ?? ""}
                  onChange={set(f)}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {(shipRates ?? []).map((r) => (
                    <MenuItem key={r.ship_rate_id} value={r.ship_rate_id}>
                      {r.description}
                      {r.country ? ` (${r.country})` : ""}
                    </MenuItem>
                  ))}
                </TextField>
              );
            }

            return (
              <TextField
                key={f.name}
                fullWidth
                label={f.label}
                type={f.type === "number" ? "number" : "text"}
                multiline={!!f.rows}
                rows={f.rows}
                style={{ gridColumn: `span ${f.col ?? 12}` }}
                value={form[f.name] ?? ""}
                onChange={set(f)}
              />
            );
          })}
        </div>
        {error && <div className={classes.error}>{error}</div>}
      </DialogContent>
      <DialogActions className={classes.actions}>
        {spec.copyFrom && (
          <>
            <Button onClick={copyFrom} disabled={busy} size="small" variant="outlined">
              {spec.copyFrom.label}
            </Button>
            <span className={classes.spacer} />
          </>
        )}
        <Button onClick={onClose} disabled={busy} size="small">
          Cancel
        </Button>
        <Button onClick={save} disabled={busy} size="small" variant="contained" color="primary">
          {busy ? "Saving…" : "Save changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default OrderPanelDialog;
